/**
 * `useWebSocketSubscription` — subscribe a handler to the shared WS
 * connection owned by `<WebSocketProvider>`. Returns the current
 * connection status so consumers can render a "Live" pill without
 * holding their own WS state.
 *
 * Replaces the pre-consolidation pattern where every realtime hook
 * (`useGlobalRealtime`, `useChatGlobalRealtime`, `useChatRealtime`,
 * `useRealtimeBoard`) called `useWebSocket` directly and opened its own
 * socket. With this hook, every consumer rides the single connection
 * the provider owns — 2-4× fewer sockets per tab, presence math stays
 * accurate (count = tabs, not tabs × hooks), and token rotation reaches
 * every consumer through one reconnect instead of N.
 *
 * The `handler` is captured in a ref so a consumer can pass an inline
 * arrow function without triggering re-subscription on every render.
 * The subscription itself attaches once per mount and detaches on
 * unmount; handler identity changes are absorbed by the ref swap.
 */

import { useEffect, useRef } from 'react'

import type { ServerMessage, WebSocketStatus } from './use-websocket'
import { useWebSocketContext } from './websocket-provider'

export function useWebSocketSubscription(
  handler: (message: ServerMessage) => void,
): WebSocketStatus {
  const ctx = useWebSocketContext()
  const handlerRef = useRef(handler)
  // Keep the ref pointing at the latest handler every render so the
  // subscription dispatches into the current closure. Without this the
  // subscriber would call the first-render handler forever and a stale
  // queryClient / projectId / kind closure would surface as
  // "events fire but the cache never updates."
  handlerRef.current = handler

  useEffect(() => {
    return ctx.subscribe((message) => {
      handlerRef.current(message)
    })
  }, [ctx])

  return ctx.status
}

/**
 * `useWebSocketSend` — accessor for the shared socket's client→server
 * `send` function without subscribing to incoming frames. Stable for the
 * lifetime of the provider (the underlying `useWebSocket.send` is a
 * `useCallback` with empty deps). No-op when the socket isn't `OPEN`.
 *
 * Used by app-semantic client messages — e.g. the chat
 * "viewing this conversation" suppression signal — that need to push a
 * frame but don't care about reading the inbound stream.
 */
export function useWebSocketSend(): (message: object) => void {
  return useWebSocketContext().send
}
